import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const schema = z.object({
  email: z.string().trim().email().max(255),
  full_name: z.string().trim().min(2).max(120),
  phone: z.string().trim().max(40).optional().default(""),
});

/**
 * Public server fn called from the auth page right after sign-up. Sends:
 *  - Welcome email (transactional queue) to the new user
 *  - SMS to admin announcing the new sign-up
 *  - SMS to the new user (if a phone was provided)
 *
 * The Supabase auth confirmation email is handled separately by the auth hook.
 */
export const notifyOnSignup = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => schema.parse(input))
  .handler(async ({ data }) => {
    const out = {
      welcome_email: false,
      user_sms: false,
      admin_sms: false,
      errors: [] as string[],
    };

    // --- Welcome email -----------------------------------------------------
    try {
      const { default: React } = await import("react");
      const { render } = await import("@react-email/components");
      const { TEMPLATES } = await import("@/lib/email-templates/registry");
      const { supabaseAdmin } = await import(
        "@/integrations/supabase/client.server"
      );

      const tmpl = TEMPLATES["welcome"];
      if (tmpl) {
        const messageId = crypto.randomUUID();
        const recipient = data.email.toLowerCase();

        const { data: suppressed } = await (supabaseAdmin as any)
          .from("suppressed_emails")
          .select("id")
          .eq("email", recipient)
          .maybeSingle();

        if (!suppressed) {
          // Ensure unsubscribe token
          let token: string | null = null;
          const { data: existing } = await (supabaseAdmin as any)
            .from("email_unsubscribe_tokens")
            .select("token,used_at")
            .eq("email", recipient)
            .maybeSingle();
          if (existing && !existing.used_at) {
            token = existing.token;
          } else if (!existing) {
            const bytes = new Uint8Array(32);
            crypto.getRandomValues(bytes);
            token = Array.from(bytes)
              .map((b) => b.toString(16).padStart(2, "0"))
              .join("");
            await (supabaseAdmin as any)
              .from("email_unsubscribe_tokens")
              .upsert(
                { token, email: recipient },
                { onConflict: "email", ignoreDuplicates: true },
              );
            const { data: stored } = await (supabaseAdmin as any)
              .from("email_unsubscribe_tokens")
              .select("token")
              .eq("email", recipient)
              .maybeSingle();
            token = stored?.token ?? token;
          }

          const element = React.createElement(tmpl.component, {
            fullName: data.full_name,
            portalUrl: "https://nkadimengdannylcs.com/portal",
            whatsappUrl: "https://wa.me/27712766198",
            phone: "071 276 6198",
          });
          const html = await render(element);
          const text = await render(element, { plainText: true });
          const subject =
            typeof tmpl.subject === "function"
              ? tmpl.subject({})
              : tmpl.subject;

          await (supabaseAdmin as any).from("email_send_log").insert({
            message_id: messageId,
            template_name: "welcome",
            recipient_email: recipient,
            status: "pending",
          });

          const { error: enqErr } = await (supabaseAdmin as any).rpc(
            "enqueue_email",
            {
              queue_name: "transactional_emails",
              payload: {
                message_id: messageId,
                to: recipient,
                from: "nkadimengdannylcs <noreply@nkadimengdannylcs.com>",
                sender_domain: "notify.nkadimengdannylcs.com",
                subject,
                html,
                text,
                purpose: "transactional",
                label: "welcome",
                idempotency_key: `welcome-${recipient}`,
                unsubscribe_token: token,
                queued_at: new Date().toISOString(),
              },
            },
          );
          if (enqErr) {
            out.errors.push(`welcome_email: ${enqErr.message}`);
          } else {
            out.welcome_email = true;
          }
        } else {
          out.errors.push("welcome_email: recipient suppressed");
        }
      }
    } catch (e) {
      console.error("[notifyOnSignup] welcome email failed", e);
      out.errors.push(`welcome_email: ${String(e)}`);
    }

    // --- SMS notifications -------------------------------------------------
    try {
      const { sendSms, ADMIN_PHONE } = await import("@/lib/sms.server");
      const adminMsg =
        `New portal sign-up — ${data.full_name}\n` +
        `Email: ${data.email}` +
        (data.phone ? `\nPhone: ${data.phone}` : "");
      const adminR = await sendSms(ADMIN_PHONE, adminMsg);
      out.admin_sms = adminR.ok;
      if (!adminR.ok && adminR.error) out.errors.push(`admin_sms: ${adminR.error}`);

      if (data.phone) {
        const firstName = data.full_name.split(" ")[0] || "there";
        const clientMsg =
          `Hi ${firstName}, welcome to Nkadimeng Danny Legal & Compliance. ` +
          `Your portal account is ready. Sign in at nkadimengdannylcs.com/portal. ` +
          `Need help? 071 276 6198.`;
        const clientR = await sendSms(data.phone, clientMsg);
        out.user_sms = clientR.ok;
        if (!clientR.ok && clientR.error)
          out.errors.push(`user_sms: ${clientR.error}`);
      }
    } catch (e) {
      console.error("[notifyOnSignup] SMS failed", e);
      out.errors.push(`sms: ${String(e)}`);
    }

    return out;
  });
